import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import type { Opt } from '@jet/environment/types/optional';
import { seoLocKeyModifier } from './utils';
import type { ClassicalShelfItem } from '@jet-app/music-classical/api/types';
import type { TrackRecordSEO } from './types';
import type { LocalizationKey } from '@jet-app/music-classical/dependencies/localization';
import { Bag } from '@jet-app/music-classical/dependencies/bag';

export function getDurationFromTracks(
    tracks: Opt<ClassicalShelfItem[]> | Opt<TrackRecordSEO[]>,
): Opt<number> {
    return tracks?.reduce((accumulator, currentValue) => {
        const duration: number =
            currentValue.duration ?? currentValue.durationMs ?? 0;
        return accumulator + duration;
    }, 0);
}

// adapted from https://github.pie.apple.com/amp-web/desktop-music-app/blob/main/app/helpers/duration-summary.js
export const formatDuration = (
    durationInSeconds: number,
    objectGraph: ObjectGraph,
): string => {
    if (durationInSeconds === 0) {
        return '';
    }

    let normalizedDuration = durationInSeconds;

    // If less than 1 minute, round up to 1 minute
    if (durationInSeconds < 60) {
        normalizedDuration = 60;
    }

    // The exact fraction of hours
    const exactHours = normalizedDuration / 3600;

    // The number of whole hours
    let hours = Math.floor(exactHours);

    // Get the number of seconds more than the whole hours
    const secondsMoreThanWholeHours = normalizedDuration % 3600;

    // Get the number of minutes, rounded to the closest minute
    // e.g. 30-60 seconds => 1 minute
    // e.g. 1-29 seconds => 0 minutes
    let minutes = Math.round(secondsMoreThanWholeHours / 60);

    // If the rounding bumped us to 60 minutes, increment the hours and reset the minutes.
    if (minutes === 60) {
        hours += 1;
        minutes = 0;
    }

    const localizer = inject(ObjectGraphType.localization, objectGraph);
    const formattedMinutes =
        minutes &&
        localizer.string(
            `ASE.Web.MusicClassicalClientJS.Minutes${seoLocKeyModifier({
                itemNumber: minutes,
            })}`,
            { count: minutes },
        );
    const formattedHours =
        hours &&
        localizer.string(
            `ASE.Web.MusicClassicalClientJS.Hours${seoLocKeyModifier({
                itemNumber: hours,
            })}`,
            { count: hours },
        );

    let result = '';
    if (hours > 0 && minutes > 0) {
        result = localizer.string(`ASE.Web.MusicClassicalClientJS.TotalTime`, {
            countHour: formattedHours,
            countMin: formattedMinutes,
        });
    } else if (hours > 0) {
        result = formattedHours;
    } else {
        result = formattedMinutes;
    }

    return result;
};

/* Converts into ISO8601 Duration Strings
 *
 * @method formatISODuration
 * @public
 * @param {Integer} seconds
 * @returns {String} durationString
 */
export function formatISODuration(duration: number): string {
    // seconds
    if (duration === 0) {
        return 'P0D';
    }

    const { hours, minutes, seconds } = getDurationParts(duration);

    return [
        'PT',
        hours && `${hours}H`,
        minutes && `${minutes}M`,
        seconds && `${seconds}S`,
    ]
        .filter(Boolean)
        .join('');
}

export function getDurationParts(sec: number): {
    hours: number;
    minutes: number;
    seconds: number;
} {
    const duration = Math.round(sec);
    return {
        hours: Math.floor(duration / 3600),
        minutes: Math.floor(duration / 60) % 60,
        seconds: duration % 60,
    };
}

// Outputs localized string in the format of "1 hour, 43 minutes"
export function deriveDuration(
    durationInMilliseconds: Opt<number>,
    objectGraph: ObjectGraph,
): Opt<string> {
    if (!durationInMilliseconds) {
        return null;
    }

    const { hours, minutes, seconds } = getDurationParts(
        Math.floor(durationInMilliseconds) / 1000,
    );

    // If less than 1 minute, round up to 1 minute
    const roundedMinutes = minutes === 0 && seconds > 0 ? 1 : minutes;

    const localization = inject(ObjectGraphType.localization, objectGraph);
    const languageTag = Bag.languageTag(objectGraph);
    const formattedHoursLocKey = (
        hours === 1
            ? 'ASE.Web.MusicClassicalClientJS.Hours.one'
            : 'ASE.Web.MusicClassicalClientJS.Hours.many'
    ) as LocalizationKey;
    const formattedHours = localization.string(formattedHoursLocKey, {
        count: hours.toLocaleString(languageTag),
    });
    const formattedMinutesLocKey = (
        roundedMinutes === 1
            ? 'ASE.Web.MusicClassicalClientJS.Minutes.one'
            : 'ASE.Web.MusicClassicalClientJS.Minutes.many'
    ) as LocalizationKey;
    const formattedMinutes = localization.string(formattedMinutesLocKey, {
        count: roundedMinutes.toLocaleString(languageTag),
    });

    let result;
    if (hours > 0 && roundedMinutes > 0) {
        result = localization.string(
            'ASE.Web.MusicClassicalClientJS.TotalTime' as LocalizationKey,
            { countHour: formattedHours, countMin: formattedMinutes },
        );
    } else if (hours > 0) {
        result = formattedHours;
    } else {
        result = formattedMinutes;
    }

    return result;
}
